import React, {
  useCallback,
  useRef,
  useState,
  useImperativeHandle
} from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import Editor from "@draft-js-plugins/editor";
import PropTypes from "prop-types";
import { useTextEditorPlugins } from "components/TextEditors/TextEditorPlugins";
import punycode from "punycode";
import { useDebounce } from "react-use";
import { generateGuid } from "core/utils/str";
import cx from "classnames";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import ClearIcon from "@mui/icons-material/Clear";
import makeStyles from "@mui/styles/makeStyles";
import styles from "assets/jss/default/components/defaultTextEditor.js";
import isNil from "lodash/isNil";
import isEqual from "lodash/isEqual";
import isFunction from "lodash/isFunction";

const useStyles = makeStyles(styles);
const DefaultTextEditor = React.forwardRef(function DefaultTextEditor(
  {
    value,
    editMode,
    cancelEditMode,
    maxLength,
    onChanged,
    editorKey,
    disableMentions,
    variant,
    name,
    className,
    onInterAddReaction
  },
  ref
) {
  const [editorState, setEditorState] = useState(
    value
      ? EditorState.createWithContent(convertFromRaw(value))
      : EditorState.createEmpty()
  );
  const [enableEditAddButton, setEnableEditAddButton] = useState(false);
  const [isExtended] = useState(variant === "extended");
  const editor = useRef();
  const defaultTextClasses = useStyles();
  function focus() {
    editor.current?.focus();
  }

  // -- non-declarative anti-pattern start --
  // Couldn't think a better way to allow the parent to reset the contents of the text editor.
  // The "declarative" way would be to allow the parent to pass in raw editor state, then the initial state should
  // be that value from the props, also it would have to be synced with the internal editor state each time it gets
  // changed from the parent component. This would be prone to bugs atm.
  // However the current setup where we don't allow initial values is much more simpler,
  // but also limiting: Editing of comments is not supported.
  useImperativeHandle(ref, () => ({
    reset: () => {
      setEditorState(EditorState.createEmpty());
    },
    focus: () => {
      focus();
    }
  }));
  // -- non-declarative anti-pattern end --
  const { plugins: editorPlugins, components, classes } = useTextEditorPlugins(
    maxLength,
    isExtended
  );
  const { MentionSuggestions, StaticToolbar, CharCounter } = components;
  const getCharCount = useCallback(() => {
    const decodeUnicode = str => punycode.ucs2.decode(str);
    const plainText = editorState.getCurrentContent().getPlainText("");
    const regex = /\r\n|\r|\n/g; // new line, carriage return, line feed
    const cleanString = plainText.replace(regex, "").trim(); // replace above characters w/ nothing
    return decodeUnicode(cleanString).length;
  }, [editorState]);

  // we delay the change update a bit, separately from the underlying editor.
  useDebounce(
    () => {
      if (maxLength > 0 && getCharCount() > maxLength) {
        return;
      }
      const editorRawState = convertToRaw(editorState.getCurrentContent());
      // This way we don't have to duplicate the logic of transforming the value change for formik's handleChange
      // callback. With the CustomEvent formik can extract the necessary information to update the state on the component.
      // You can get more idea about this by reading formik's source code at:
      // https://github.com/formium/formik/blob/31405abfc9373b2236eecf0f34f630906579e193/packages/formik/src/Formik.tsx#L610
      const evt = new CustomEvent("change", {
        detail: {
          id: editorKey
        }
      });
      Object.defineProperty(evt, "target", {
        writable: false,
        value: {
          name, // formik uses this to determine where the value change goes
          id: editorKey, // if name field is not defined formik falls back to this attribute to determine where the value should go
          type: "", // formik uses this to determine if parsing is required for the value, if falsy or empty string, value is taken as raw
          value: editorRawState
        }
      });

      isEqual(value, editorRawState)
        ? setEnableEditAddButton(false)
        : setEnableEditAddButton(true);
      onChanged(evt);
    },
    80,
    [editorState, maxLength]
  );

  function handleEditorChange(editorState) {
    setEditorState(editorState);
  }

  const checkFeedComment =
    !isNil(onInterAddReaction) && isFunction(onInterAddReaction);

  return (
    <div onClick={focus} className={cx([classes.editor, className])}>
      <Editor
        editorKey={editorKey}
        editorState={editorState}
        onChange={handleEditorChange}
        plugins={editorPlugins}
        ref={e => {
          editor.current = e;
        }}
      />
      <div
        className={checkFeedComment ? defaultTextClasses.toolbarWrapper : null}
      >
        <StaticToolbar />
        {checkFeedComment ? (
          <IconButton
            className={defaultTextClasses.PostButton}
            disabled={
              editMode
                ? !enableEditAddButton
                : editorState.getCurrentContent().getPlainText().length === 0 ||
                  editorState
                    .getCurrentContent()
                    .getPlainText()
                    .trim() === ""
            }
            onClick={() => {
              onInterAddReaction();
              setEditorState(EditorState.createEmpty());
            }}
            size="large"
          >
            <SendIcon />
          </IconButton>
        ) : null}
        {editMode ? (
          <IconButton
            className={defaultTextClasses.PostButton}
            onClick={cancelEditMode}
            size="large"
          >
            <ClearIcon />
          </IconButton>
        ) : null}
      </div>
      {maxLength > 0 ? (
        <div>
          <CharCounter limit={maxLength} />
          &nbsp;/&nbsp;{maxLength}
        </div>
      ) : null}
      <MentionSuggestions disabled={disableMentions} />
    </div>
  );
});

DefaultTextEditor.propTypes = {
  value: PropTypes.object,
  editMode: PropTypes.bool,
  cancelEditMode: PropTypes.func,
  maxLength: PropTypes.number,
  onChanged: PropTypes.func.isRequired,
  onInterAddReaction: PropTypes.func,
  editorKey: PropTypes.string.isRequired,
  disableMentions: PropTypes.bool,
  variant: PropTypes.oneOf(["small", "extended"]),
  name: PropTypes.string.isRequired,
  className: PropTypes.any
};

DefaultTextEditor.defaultProps = {
  extendedByDefault: false,
  editMode: false,
  maxLength: 0,
  disableMentions: false,
  variant: "small",
  name: generateGuid()
};

export default DefaultTextEditor;
